import BaseMap from "@arcgis/core/Basemap";
import Camera from "@arcgis/core/Camera";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Point from "@arcgis/core/geometry/Point";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import SolidEdges3D from "@arcgis/core/symbols/edges/SolidEdges3D";
import FillSymbol3DLayer from "@arcgis/core/symbols/FillSymbol3DLayer";
import MeshSymbol3D from "@arcgis/core/symbols/MeshSymbol3D";
import { ARCGIS_BASEMAP_ID_BY_MAP_STYLE, WORLD_MAP_SINGLE_ENTITY_ZOOM } from "@components/world-map/world-map-constants";
import { getLayer, getMainClusterEntities, getWebMercatorGeometries, isAbortError, runGoTo } from "@components/world-map/world-map-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
// eslint-disable-next-line no-restricted-imports -- ArcGIS draws on a canvas, where a CSS variable does not resolve
import { grey } from "@flat-ui";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Prepares the 3D scene's environment. The `colored` style uses the `topo-3d`
 * basemap, which already includes 3D buildings, trees, and place labels, so no
 * scene layers are added manually here. This sets the ground to real-world
 * elevation for terrain relief and lights the scene evenly from the camera's
 * viewpoint (VirtualLighting) instead of by the sun, so a project is always
 * brightly lit and readable regardless of its real-world time of day. This
 * intentionally drops the day/night terminator (which left projects in nighttime
 * timezones dark and hard to see) and the sun shadows.
 */
function setup3DScene(view) {
  const map = view.map;
  if (!map) {
    return;
  }
  map.ground = "world-elevation";
  view.environment.lighting = {
    type: "virtual"
  };
}

/**
 * Recolors the `topo-3d` basemap's 3D buildings from the default near-white to a
 * grey with thin dark-gray edges, so buildings read as a subtle backdrop
 * rather than competing with the project markers. The buildings arrive as a
 * 3D-object mesh `SceneLayer` (title "Buildings"), which supports a `MeshSymbol3D`
 * fill plus solid edges. No-op when the layer isn't present (e.g. the 2D `hybrid`
 * basemap, or a future change to the basemap's layer set).
 */
function styleBasemapBuildings(basemap) {
  const buildings = basemap.baseLayers.find(layer => layer.type === "scene" && layer.title === "Buildings");
  if (!buildings) {
    return;
  }

  // ArcGIS renders the scene with WebGL, not CSS, so these colours cannot be palette variables.
  buildings.renderer = new SimpleRenderer({
    symbol: new MeshSymbol3D({
      symbolLayers: [new FillSymbol3DLayer({
        material: {
          color: grey[200],
          colorMixMode: "replace"
        },
        edges: new SolidEdges3D({
          color: grey[300],
          size: 1
        })
      })]
    })
  });
}
/**
 * Initializes and manages the ArcGIS Map and GeoJSONLayer lifecycle.
 * Loads the basemap style, creates the layer from the provided entities,
 * updates the layer when entities change, and destroys the map on unmount.
 *
 * Supports both the 2D `MapView` (`<arcgis-map>`) and the 3D `SceneView`
 * (`<arcgis-scene>`). When `is3DEnabled` toggles, the parent swaps the web
 * component; this hook resets its readiness so the dependent hooks re-subscribe
 * to the newly created view, and preserves the previously viewed area.
 *
 * @returns Refs and handlers needed to wire up the `<arcgis-map>` / `<arcgis-scene>` web component.
 */
export function useMapInitialization({
  entities,
  fields,
  mapStyle,
  is3DEnabled,
  initialView
}) {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const [isViewReady, setIsViewReady] = useState(false);
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);
  const blobUrlRef = useRef(null);
  const mapStyleRef = useRef(mapStyle);

  // Latest 3D flag, read inside the (stable) ArcGIS callbacks without re-creating them.
  const is3DEnabledRef = useRef(is3DEnabled);
  is3DEnabledRef.current = is3DEnabled;

  // Center/zoom of the last settled view, used to preserve the viewed area when
  // toggling between 2D and 3D (which recreates the underlying view).
  const lastViewStateRef = useRef(null);

  // Whether the view has already been fitted to the entities once. The view can
  // become ready before the projects have loaded from the store, so the initial
  // fit is deferred until entities are available (see the effect below).
  const hasInitialFitRef = useRef(false);

  // Set true right before the map applies its initial view programmatically
  // (deep-link, 2D/3D restore, or fit to projects). The view-sync watcher reads
  // and clears it so that settle is not persisted to the URL as if the user had
  // navigated there.
  const skipNextViewSyncRef = useRef(false);

  // Reads and clears the skip flag. Exposed as a callback (rather than the raw
  // ref) so the view-sync watcher does not mutate a hook argument, which the
  // React compiler disallows; the ref is only mutated here, where it is owned.
  const consumeSkipNextViewSync = useCallback(() => {
    const shouldSkip = skipNextViewSyncRef.current;
    skipNextViewSyncRef.current = false;
    return shouldSkip;
  }, []);

  // LODs captured from the initial basemap (gray-vector). Forced on every
  // basemap swap so all styles share the exact same zoom levels. 2D only.
  const baseLODsRef = useRef(null);

  /**
   * Builds a fresh GeoJSON layer configured for custom popup handling.
   */
  const createConfiguredLayer = useCallback(() => {
    const {
      layer: nextLayer,
      blobUrl: nextBlobUrl
    } = getLayer({
      entities,
      fields,
      is3DEnabled: is3DEnabledRef.current
    });
    blobUrlRef.current = nextBlobUrl;
    nextLayer.popupEnabled = false;
    return nextLayer;
  }, [entities, fields]);

  /**
   * Loads and applies the named ArcGIS basemap for the given style.
   * Awaits basemap.load() so callers can render markers only after the basemap is ready.
   */
  const setupMapBasemap = useCallback(async style => {
    if (!mapRef.current) {
      return;
    }
    const fromId = BaseMap.fromId(ARCGIS_BASEMAP_ID_BY_MAP_STYLE[style]);
    if (!fromId) {
      throw new Error(`Unknown ArcGIS basemap id for style "${style}".`);
    }
    // ArcGIS draws `referenceLayers` (roads, labels, boundaries) above
    // operational layers, which would cover our markers on the `hybrid`
    // basemap. Rebuild the basemap with every layer in `baseLayers` so
    // markers always render on top while labels stay visible.
    await fromId.load();
    const nextBasemap = new BaseMap({
      baseLayers: [...fromId.baseLayers.toArray(), ...fromId.referenceLayers.toArray()]
    });
    mapRef.current.basemap = nextBasemap;
    await nextBasemap.load();
    styleBasemapBuildings(nextBasemap);

    // basemap.load() only loads metadata; tiles still need to render.
    // Wait for the view to settle (`view.updating === false`) so callers
    // know the new basemap is actually visible on screen.
    if (viewRef.current) {
      await reactiveUtils.whenOnce(() => viewRef.current?.updating === false);
    }
  }, []);

  /**
   * Removes the previous data layer (if any), revokes its blob URL, and adds a freshly
   * built layer to the map. Used both for the initial setup and whenever the layer
   * needs to be rebuilt (e.g. entities or fields changed).
   */
  const replaceDataLayer = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    if (layerRef.current) {
      mapRef.current.remove(layerRef.current);
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    layerRef.current = createConfiguredLayer();
    mapRef.current.add(layerRef.current);
  }, [createConfiguredLayer]);

  /**
   * Fits the view to the current entities so the map opens centered on the projects:
   * no entities -> leave the current view; one -> focus zoom; many -> frame the pins.
   * In 3D only the main cluster is framed (far-flung outliers are ignored) so the globe
   * doesn't zoom out to a whole-world view centered on the ocean. Skips animation so the
   * correct position shows immediately, and records that the initial fit has happened so
   * it is not repeated.
   * Returns the `goTo` promise (or `undefined` when there is nothing to fit).
   */
  const fitViewToEntities = useCallback(() => {
    const view = viewRef.current;
    if (!view || entities.length === 0) {
      return undefined;
    }
    const entitiesToFit = view.type === "3d" ? getMainClusterEntities(entities) : entities;
    const geometries = getWebMercatorGeometries(entitiesToFit);
    hasInitialFitRef.current = true;
    skipNextViewSyncRef.current = true;
    const goToOptions = {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API
      animate: false
    };
    if (geometries.length === 1) {
      return view.goTo({
        target: geometries[0],
        zoom: WORLD_MAP_SINGLE_ENTITY_ZOOM
      }, goToOptions);
    }
    return view.goTo(geometries, goToOptions);
  }, [entities]);

  /**
   * Sets up the initial map view.
   * Priority: (1) restore the previously viewed area when the view is recreated by a
   * 2D/3D toggle; (2) apply the deep-link `initialView` on first load; (3) otherwise
   * fit to the entities (or leave the default world view when none are loaded yet;
   * the deferred initial-fit effect handles that once entities arrive).
   */
  async function setupView() {
    const goToOptions_0 = {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API
      animate: false
    };
    const lastViewState = lastViewStateRef.current;
    if (lastViewState) {
      hasInitialFitRef.current = true;
      skipNextViewSyncRef.current = true;
      await viewRef.current?.goTo({
        center: lastViewState.center,
        zoom: lastViewState.zoom
      }, goToOptions_0);
      return;
    }
    if (initialView) {
      hasInitialFitRef.current = true;
      skipNextViewSyncRef.current = true;
      // Restore the exact camera pose from the deep link. Using the camera
      // altitude (rather than a zoom level) reproduces the framing precisely,
      // independent of the viewport size at load time.
      const camera = new Camera({
        position: new Point({
          longitude: initialView.lng,
          latitude: initialView.lat,
          z: initialView.altitude
        }),
        heading: initialView.heading,
        tilt: initialView.tilt
      });
      await viewRef.current?.goTo(camera, goToOptions_0);
      return;
    }
    await fitViewToEntities();
  }

  /**
   * Handles the ArcGIS view-ready event and performs initial map setup.
   */
  async function handleViewReady(event) {
    const {
      view: view_0
    } = event.target;
    const {
      map
    } = view_0;
    try {
      if (!map) {
        throw new Error("Error loading the map instance.");
      }
      viewRef.current = view_0;
      mapRef.current = map;

      // LOD/zoom constraints only exist on the 2D MapView. Capture and force the
      // initial basemap's LODs so all 2D styles share identical zoom levels, and
      // snap to integer zoom levels for consistent zoom-out behavior across styles.
      if (view_0.type === "2d") {
        baseLODsRef.current = view_0.constraints.effectiveLODs ? [...view_0.constraints.effectiveLODs] : null;
        view_0.constraints = {
          ...view_0.constraints,
          /* eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API */
          snapToZoom: true
        };
      } else {
        setup3DScene(view_0);
      }

      // Start positioning the camera and loading the basemap WITHOUT blocking the
      // markers on either. The camera `goTo` waits for terrain to settle at the
      // target, and the basemap paint (elevation + 3D buildings/trees) can take a
      // few seconds; kicking both off here and adding the data layer in parallel
      // lets the project markers appear immediately instead of after the camera
      // has settled and the whole 3D scene has finished painting.
      //
      // `setupView` marks the initial fit as done synchronously (before its first
      // await), so flipping `isViewReady` below does not trigger a second fit from
      // the deferred initial-fit effect.
      const viewReady = setupView();
      const basemapReady = setupMapBasemap(mapStyle);

      // Keep the last settled center/zoom so the viewed area survives a 2D/3D toggle.
      reactiveUtils.watch(() => view_0.stationary, isStationary => {
        const longitude = view_0.center?.longitude;
        const latitude = view_0.center?.latitude;
        if (isStationary && typeof longitude === "number" && typeof latitude === "number") {
          lastViewStateRef.current = {
            center: [longitude, latitude],
            zoom: view_0.zoom
          };
        }
      });

      // Show the markers and mark the view ready right away, in parallel with the
      // camera positioning and basemap paint, so pins and map controls appear
      // without waiting for the full 3D scene to finish rendering. Only the 2D
      // map uses the GeoJSON data layer; in 3D the clustering layers draw the
      // markers as soon as the view is ready.
      if (view_0.type === "2d") {
        replaceDataLayer();
      }
      setIsViewReady(true);

      // Await the camera and basemap so the (2D-only) LOD re-apply runs once the
      // basemap is painted and any load error is still surfaced. This does not
      // delay the markers.
      await Promise.all([viewReady, basemapReady]);

      // Re-apply the LODs from the initial basemap after the style switch
      // to ensure the new basemap doesn't introduce extra zoom levels. 2D only.
      if (view_0.type === "2d" && baseLODsRef.current) {
        view_0.constraints = {
          ...view_0.constraints,
          lods: baseLODsRef.current
        };
      }
    } catch (error) {
      // Leaving the page mid-load destroys the map/view, so pending ArcGIS work fails
      // with an AbortError or a TypeError (e.g. reading `navigationConstraint` of a
      // null ground). That is expected and must not show a toast or reach Sentry.
      if (isAbortError(error) || view_0.destroyed || map?.destroyed) {
        return;
      }
      handleErrorWithToast({
        id: `handleViewReady-${Date.now().toString()}`,
        title: "Error initializing the map.",
        error
      });
    }
  }

  // Recreate the data layer when entities or layer fields change.
  useEffect(() => {
    if (mapRef.current && layerRef.current) {
      replaceDataLayer();
    }
  }, [replaceDataLayer]);

  // Fit the view to the projects once they first load. The view often becomes
  // ready before the entities arrive from the store; this re-fits as soon as
  // entities are available so the map opens centered on the projects. Runs only
  // until the first successful fit (the 2D/3D toggle restore and the deep-link
  // initial view are handled separately in setupView).
  useEffect(() => {
    if (!isViewReady || hasInitialFitRef.current) {
      return;
    }
    runGoTo(fitViewToEntities());
  }, [isViewReady, fitViewToEntities]);

  // When the 2D/3D mode toggles, the parent swaps the `<arcgis-map>` /
  // `<arcgis-scene>` web component, which destroys the current view and creates
  // a new one. Reset readiness and drop stale refs so the dependent hooks tear
  // down their listeners and re-subscribe to the new view once it is ready.
  const isFirstModeRender = useRef(true);
  useEffect(() => {
    if (isFirstModeRender.current) {
      isFirstModeRender.current = false;
      return;
    }
    setIsViewReady(false);
    viewRef.current = null;
    layerRef.current = null;
    mapRef.current = null;
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, [is3DEnabled]);

  // Re-apply the basemap and recolor the marker renderer when the requested style changes
  // after the view is ready. Mirrors the initial load order: basemap finishes loading first,
  // then the marker color is updated so the new color appears in sync with the new basemap.
  // The data layer itself is reused (no cluster/feature reset).
  //
  // While the new basemap is loading, ArcGIS clears the old tiles, which would otherwise
  // leave markers floating on a white canvas. We hide the data layer for the duration of
  // the swap and re-show it once the new basemap is ready.
  useEffect(() => {
    // Wait for the map view to be ready before listening for map style changes.
    if (!isViewReady) {
      return;
    }

    // Early exit if the new style is the same as the current/cached style reference.
    // This prevents any unnecessary map re-render when the `mapStyle` prop is updated with
    // the same value or when the map has loaded for the first time and the flag `isViewReady` changes to true.
    if (mapStyleRef.current === mapStyle) {
      return;
    }

    // Update the cached map style reference to the new value.
    mapStyleRef.current = mapStyle;
    let isCancelled = false;
    const dataLayer = layerRef.current;
    if (dataLayer) {
      dataLayer.visible = false;
    }
    void (async () => {
      try {
        await setupMapBasemap(mapStyle);
        if (isCancelled) {
          return;
        }

        // Re-enforce the LODs from the initial basemap and ensure integer
        // zooming is still enabled, so the new basemap cannot introduce extra
        // zoom levels that differ from the colored style. 2D only.
        const view_1 = viewRef.current;
        if (view_1?.type === "2d") {
          view_1.constraints = {
            ...view_1.constraints,
            /* eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API */
            snapToZoom: true,
            lods: baseLODsRef.current ?? undefined
          };
        }

        // The marker color is style-independent, so the renderer doesn't need
        // rebuilding on a style change; just re-show the data layer that was
        // hidden during the basemap swap (2D only; 3D has no data layer).
        if (layerRef.current) {
          layerRef.current.visible = true;
        }
      } catch (error_0) {
        // Ensure the layer is visible again even if the basemap swap failed,
        // so the user is not left without markers indefinitely.
        if (layerRef.current) {
          layerRef.current.visible = true;
        }
        handleErrorWithToast({
          id: `setupMapBasemap-${Date.now().toString()}`,
          title: "Error switching the map style.",
          error: error_0
        });
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [mapStyle, isViewReady, setupMapBasemap, handleErrorWithToast]);

  // Destroy map resources when the hook owner unmounts.
  useEffect(() => {
    return () => {
      if (viewRef.current?.container) {
        viewRef.current.container.style.cursor = "default";
      }
      mapRef.current?.destroy();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);
  return {
    viewRef,
    layerRef,
    isViewReady,
    handleViewReady,
    consumeSkipNextViewSync
  };
}